import * as R from "ramda";
import React, { useEffect, useState, useRef } from "react";
import { connect } from "react-redux";
import { bindInstance } from "redux-mvc";
import { SketchPicker } from "react-color";

import { noop } from "utils/General";
import { Div, Popover, DownIcon, Text3, CloseIcon } from "components/Base";

import { actions } from "ui-kit/Form/model";
import { getValue } from "ui-kit/Form/selectors";
import {
  FIELD_TYPES,
  DEFAULT_PRESET_SKETCH_COLORS
} from "ui-kit/Form/constants";

import { WithFormInstanceConsumer } from "ui-kit/Form/View/Context";

const spanStyles = {
  alignSelf: "stretch",
  backgroundColor: "hsl(0, 0%, 80%)",
  marginBottom: "8px",
  marginTop: "8px",
  width: "1px",
  boxSizing: "border-box",
  color: "#ccc"
};

const DownRightIcon = () => (
  <Div display="row.center.center" style={{ gap: "8px", height: "100%" }}>
    <span style={spanStyles}></span>
    <DownIcon color="#ccc" size={25} />
  </Div>
);

const DefaultButton = ({
  onClick,
  color = "#FFF",
  showColor = false,
  labelRef,
  onFocus,
  fieldId,
  fieldType
}) => {
  const [isActive, focus] = useState(false);
  return (
    <div
      display="row.flex-start.center"
      onClick={onClick}
      color="gray7"
      height={38}
      px={2}
      onBlur={() => focus(false)}
      onFocus={() => {
        focus(true);
        onFocus(null, { meta: { fieldId, fieldType } });
      }}
      style={{
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "center",
        gap: "8px",
        border: isActive ? "1px solid #2684FF" : "1px solid #E7E6EA",
        boxShadow: isActive ? "0 0 0 1px #2684FF" : "unset",
        borderRadius: "4px",
        paddingLeft: "8px",
        paddingRight: "8px",
        height: "38px",
        background: "#F8F8F8",
        color: "#494949"
      }}
      tabIndex={0}
      onKeyPress={e => {
        if (e.which === 13) {
          e.preventDefault();
          return onClick(e);
        }
        return e;
      }}
      ref={labelRef}
    >
      {showColor && color && <Div height={20} width={20} bra={3} bg={color} />}
      {showColor && color && (
        <Text3 fw={2} uppercase flex={1}>
          {color}
        </Text3>
      )}
      {showColor && !color && (
        <Text3 fw={1} color="gray6" flex={1}>
          Select a color
        </Text3>
      )}
      <DownRightIcon />
    </div>
  );
};

const decorate = R.compose(
  WithFormInstanceConsumer({ fieldType: FIELD_TYPES.COLOR }),
  connect(
    (state, props) => ({
      value: getValue(state, props)
    }),
    bindInstance({
      onChange: actions.setFieldValue,
      setIniValue: actions.setIniValue
    })
  )
);

const UColorSketchPicker = ({
  value = "",
  iniValue,
  fieldId,
  fieldType,
  onChange = noop,
  setIniValue = noop,
  LabelComponent = DefaultButton,
  showColor = false,
  presetColors = DEFAULT_PRESET_SKETCH_COLORS,
  innerSpanStyle = {},
  onFocus = noop
}) => {
  useEffect(() => {
    setIniValue(iniValue, {
      meta: { fieldType, fieldId }
    });
  }, []);

  const labelRef = useRef(null);

  return (
    <Div
      display="row.flex-start.flex-start"
      width={1}
      style={{ background: "transparent" }}
    >
      <Popover
        anchorOrigin={{ horizontal: "center", vertical: "center" }}
        Label={({ onClick }) => (
          <LabelComponent
            color={value}
            onClick={onClick}
            showColor={showColor}
            labelRef={labelRef}
            onFocus={onFocus}
            fieldId={fieldId}
            fieldType={fieldType}
          />
        )}
        innerSpanStyle={innerSpanStyle}
        onClose={() => {
          if (labelRef && labelRef?.current) {
            labelRef.current.focus();
          }
        }}
      >
        {({ closePopover }) => (
          <Div bg={"white"} display="row.flex-end.flex-start">
            <SketchPicker
              styles={{ picker: { padding: "16px", width: "250px" } }}
              color={value ? `${value}` : undefined}
              onChange={color => {
                onChange(color.hex, {
                  meta: { fieldType, fieldId }
                });
              }}
              presetColors={presetColors}
            />
            <CloseIcon
              size={18}
              uppercase
              color="black"
              style={{ cursor: "pointer", position: "absolute" }}
              onClick={() => {
                closePopover();
                if (labelRef && labelRef?.current) {
                  labelRef.current.focus();
                }
              }}
            />
          </Div>
        )}
      </Popover>
    </Div>
  );
};

export const ColorSketchPicker = decorate(UColorSketchPicker);
